import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { formatXAF } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
  LineChart, Line, CartesianGrid,
} from "recharts";
import { BarChart3 } from "lucide-react";

const PALETTE = ["#1E3F20", "#4A7C59", "#CC5500", "#DDA15E", "#3A86FF", "#2A9D8F"];

export default function Analytics() {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-6">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <div className="label-eyebrow mb-2">AgriNova Intelligence</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("analytics_page")}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" />{t("revenue_monthly")}</h3>
            <div className="h-56">
              <ResponsiveContainer>
                <LineChart data={data.revenue_by_month}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatXAF(v)} />
                  <Line type="monotone" dataKey="revenue" stroke="#1E3F20" strokeWidth={2.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">{t("sales_by_product")}</h3>
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={data.sales_by_product} layout="vertical">
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatXAF(v)} />
                  <Bar dataKey="total" radius={[0, 6, 6, 0]}>
                    {data.sales_by_product.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">{t("mortality_by_pond")}</h3>
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={data.mortality_by_pond}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="mortality" fill="#CC5500" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">{t("cost_by_task")}</h3>
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={data.cost_by_task_type} layout="vertical">
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} tickFormatter={(v) => t(`task_${v}`) || v} />
                  <Tooltip formatter={(v) => formatXAF(v)} />
                  <Bar dataKey="cost" fill="#4A7C59" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
